/**
 * Analytics — نظام تتبع الإحصائيات الحقيقية
 * يسجّل مشاهدات الصفحات والمقالات والزوار في Supabase
 */

import { supabase } from "@/lib/supabase";

// ── Session ID ──────────────────────────────────────────────
// معرّف جلسة فريد لكل زيارة (يُحفظ في sessionStorage)
function getSessionId(): string {
  const key = "alsiyahiah_sid";
  let sid = sessionStorage.getItem(key);
  if (!sid) {
    sid = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(key, sid);
  }
  return sid;
}

// ── تسجيل مشاهدة صفحة ──────────────────────────────────────
export async function trackPageView(pagePath: string, articleSlug?: string): Promise<void> {
  try {
    const sessionId = getSessionId();
    await supabase.from("page_views").insert({
      page_path: pagePath,
      article_slug: articleSlug ?? null,
      session_id: sessionId,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent.slice(0, 200),
    });
  } catch {
    // silent — لا نوقف الموقع بسبب خطأ في الإحصائيات
  }
}

// ── تسجيل مشاهدة مقال ──────────────────────────────────────
// يُسجّل مرة واحدة فقط لكل جلسة لكل مقال
export async function trackArticleView(articleSlug: string, articleId?: number): Promise<void> {
  try {
    const sessionId = getSessionId();
    // upsert — يتجاهل إذا كانت الجلسة شاهدت المقال مسبقاً
    const { error } = await supabase.from("article_views").insert({
      article_slug: articleSlug,
      article_id: articleId ?? null,
      session_id: sessionId,
    });
    // إذا نجح الإدراج (لم يكن مكرراً) → حدّث عداد المقال
    if (!error) {
      await supabase.rpc("increment_article_views", { slug: articleSlug });
    }
  } catch {
    // silent
  }
}

// ── تسجيل إعجاب بمقال ──────────────────────────────────────
export async function trackArticleLike(articleSlug: string, liked: boolean): Promise<void> {
  try {
    const key = `like_${articleSlug}`;
    const wasLiked = localStorage.getItem(key) === "1";
    if (liked && !wasLiked) {
      localStorage.setItem(key, "1");
      await supabase.rpc("increment_article_likes", { slug: articleSlug });
    } else if (!liked && wasLiked) {
      localStorage.removeItem(key);
      await supabase.rpc("decrement_article_likes", { slug: articleSlug });
    }
  } catch {
    // silent
  }
}

// ── قراءة حالة الإعجاب ──────────────────────────────────────
export function getArticleLikeState(articleSlug: string): boolean {
  return localStorage.getItem(`like_${articleSlug}`) === "1";
}

// ── إحصائيات الـ Dashboard ──────────────────────────────────
export interface DashboardAnalytics {
  todayVisitors: number;
  todayPageViews: number;
  weekVisitors: number;
  weekPageViews: number;
  monthVisitors: number;
  monthPageViews: number;
  totalArticleViews: number;
  topArticles: { slug: string; title: string; views: number; image: string }[];
  viewsByDay: { day: string; views: number; visitors: number }[];
}

export async function fetchDashboardAnalytics(): Promise<DashboardAnalytics> {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
  const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

  const [todayRes, weekRes, monthRes, topArticlesRes, viewsByDayRes] = await Promise.all([
    // اليوم
    supabase
      .from("page_views")
      .select("session_id", { count: "exact" })
      .gte("created_at", todayStart),
    // الأسبوع
    supabase
      .from("page_views")
      .select("session_id", { count: "exact" })
      .gte("created_at", weekStart),
    // الشهر
    supabase
      .from("page_views")
      .select("session_id", { count: "exact" })
      .gte("created_at", monthStart),
    // أكثر المقالات مشاهدة
    supabase
      .from("articles")
      .select("slug, title, views_count, image")
      .neq("status", "draft")
      .order("views_count", { ascending: false })
      .limit(5),
    // مشاهدات آخر 7 أيام
    supabase
      .from("page_views")
      .select("created_at, session_id")
      .gte("created_at", weekStart)
      .order("created_at", { ascending: true }),
  ]);

  // حساب الزوار الفريدين (unique sessions)
  const todayData = todayRes.data || [];
  const weekData = weekRes.data || [];
  const monthData = monthRes.data || [];

  const todayVisitors = new Set(todayData.map((r) => r.session_id)).size;
  const weekVisitors = new Set(weekData.map((r) => r.session_id)).size;
  const monthVisitors = new Set(monthData.map((r) => r.session_id)).size;

  // مشاهدات آخر 7 أيام مجمّعة حسب اليوم
  const dayMap: Record<string, { views: number; sessions: Set<string> }> = {};
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
    const key = d.toLocaleDateString("en-US", { weekday: "short" });
    dayMap[key] = { views: 0, sessions: new Set() };
  }

  (viewsByDayRes.data || []).forEach((row) => {
    const d = new Date(row.created_at);
    const key = d.toLocaleDateString("en-US", { weekday: "short" });
    if (dayMap[key]) {
      dayMap[key].views++;
      dayMap[key].sessions.add(row.session_id);
    }
  });

  const viewsByDay = Object.entries(dayMap).map(([day, val]) => ({
    day,
    views: val.views,
    visitors: val.sessions.size,
  }));

  // إجمالي مشاهدات المقالات
  const totalArticleViews = (topArticlesRes.data || []).reduce(
    (sum, a) => sum + (a.views_count || 0),
    0
  );

  return {
    todayVisitors,
    todayPageViews: todayData.length,
    weekVisitors,
    weekPageViews: weekData.length,
    monthVisitors,
    monthPageViews: monthData.length,
    totalArticleViews,
    topArticles: (topArticlesRes.data || []).map((a) => ({
      slug: a.slug,
      title: a.title,
      views: a.views_count || 0,
      image: a.image,
    })),
    viewsByDay,
  };
}
